import { Avatar, Chip } from '@material-ui/core';
import { CheckCircle as CheckIcon, Warning as WarningIcon } from '@material-ui/icons';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';

import { Academy } from '@aps-management/primapp-common';

import masterClient from '_utils/masterClient';

/* */
const styles = (theme) => ({
  chip: {
    marginTop: theme.spacing.unit * 2,
    marginRight: theme.spacing.unit * 1,
  },
  chipExceeded: {
    marginTop: theme.spacing.unit * 2,
    marginRight: theme.spacing.unit * 1,
  },
  labelMore: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing.unit * 2,
  },
});

const RenderChip = ({
  classes,
  entrant,
  handleDeleteEntrant,
  setListExceededQuotaPlayers,
  setOpenPartners,
  matchRoute,
  index,
  golf,
  lessonDate,
}) => {
  const { data: quotaData } = useQuery(
    ['academy-lesson-quotas', entrant?.id, matchRoute ? 'INDIVIDUAL' : 'GROUP'],
    () =>
      Academy.api.getLessonRegistrationsQuota(masterClient, {
        customerId: entrant?.id,
        lessonType: matchRoute ? 'INDIVIDUAL' : 'GROUP',
      }),
    {
      enabled: !!entrant?.id,
      retry: 0,
      cacheTime: 0,
      initialData: () => ({
        quotas: 1,
        isExceeded: false,
        activeRegistrations: [],
        __typename: 'LessonRegistrationQuotaResponse',
      }),
    },
  );

  // Récupérer maxInDay depuis les options du golf
  const maxInDay = useMemo(() => {
    const restrictions = matchRoute
      ? golf?.options?.academy?.onlineRestrictions?.individual
      : golf?.options?.academy?.onlineRestrictions?.group;
    return restrictions?.maxInDay;
  }, [golf, matchRoute]);

  // Calculer si maxInDay est dépassé pour aujourd'hui
  const isMaxInDayExceeded = useMemo(() => {
    // Si maxInDay n'est pas défini ou vaut -1, pas de limite
    if (!maxInDay || maxInDay === -1) {
      return false;
    }

    // Filtrer les réservations pour ne garder que celles du jour du cours
    const lessonDay = moment(lessonDate);
    const todayStart = lessonDay.clone().startOf('day');
    const todayEnd = lessonDay.clone().endOf('day');

    const registrationsToday = (quotaData?.activeRegistrations || []).filter((registration) => {
      const lessonStart = moment(registration.lesson.startsAt);
      return lessonStart.isBetween(todayStart, todayEnd, null, '[]');
    });

    return registrationsToday.length >= maxInDay;
  }, [quotaData, maxInDay, lessonDate]);

  useEffect(() => {
    // Vérifier les deux conditions : quota période OU limite journalière
    if (quotaData?.isExceeded || isMaxInDayExceeded) {
      setListExceededQuotaPlayers((prev) => [...prev.filter((p) => p.id !== entrant?.id), entrant]);
    } else {
      setListExceededQuotaPlayers((prev) => prev.filter((p) => p.id !== entrant?.id));
    }
  }, [entrant, quotaData, isMaxInDayExceeded, setListExceededQuotaPlayers]);

  const isOwner = entrant?.type === 'owner';
  const isAnonymous = entrant?.type === 'anonymous';
  const isDeletable = entrant?.type !== 'owner' && entrant?.type !== 'anonymous';

  const fullname = isAnonymous ? 'Participant anonyme' : `${entrant?.firstname} ${entrant?.lastname}`;

  const hasExceeded = quotaData?.isExceeded || isMaxInDayExceeded;

  const customProps = {
    label: fullname,
    clickable: true,
    color: isOwner ? 'primary' : 'secondary',
    style: hasExceeded
      ? {
          backgroundColor: '#d3772b',
        }
      : styles.chip,
    avatar: (
      <Avatar
        style={{
          backgroundColor: hasExceeded ? '#a34700' : 'unset',
        }}
      >
        {hasExceeded ? <WarningIcon /> : <CheckIcon />}
      </Avatar>
    ),
  };

  if (isAnonymous) {
    customProps.avatar = null;
    customProps.variant = 'outlined';
    customProps.onClick = () => setOpenPartners(true);
  }
  if (isOwner) {
    customProps.color = 'primary';
    customProps.clickable = false;
  }
  if (isDeletable) {
    customProps.onDelete = () => handleDeleteEntrant(index);
  }

  return <Chip className={classes.chip} {...customProps} />;
};

export default RenderChip;
